import React from 'react';
import { Skeleton } from 'antd';
import { AuthorizedUsersSkeleton } from './AuthorizedUsersSkeleton';
import './AccessVerificationSkeleton.css';

type Props = {
  /** Inside the Settings → Whitelist tab: no page heading, matching the
   *  `embedded` prop on AccessVerification. */
  embedded?: boolean;
};

/**
 * Loading state for Access & Verification. Mirrors the real layout — access
 * policy radio cards and the authorized-users table — so nothing shifts when
 * the data lands.
 */
export const AccessVerificationSkeleton: React.FC<Props> = ({ embedded = false }) => (
  <div className="access-verification-skeleton">
    {!embedded && (
      <div className="access-verification-skeleton__heading">
        <Skeleton.Input active style={{ width: 260, minWidth: 260, height: 28 }} />
        <Skeleton.Input active size="small" style={{ width: 420, minWidth: 420, height: 16 }} />
      </div>
    )}

    {/* Access policy */}
    <section className="access-policy-section">
      <Skeleton.Input active style={{ width: 140, minWidth: 140, height: 22, marginBottom: 8 }} />
      <Skeleton.Input active size="small" style={{ width: 300, minWidth: 300, height: 16 }} />

      <div className="access-verification-skeleton__policies">
        <PolicyCardSkeleton descWidth={340} />
        <PolicyCardSkeleton descWidth={390} />
      </div>
    </section>

    {/* End User Access — card head; the rows come from
        AuthorizedUsersSkeleton, which the real component renders itself. */}
    <section className="access-verification-skeleton__users">
      <div className="access-verification-skeleton__users-head">
        <div className="access-verification-skeleton__users-title">
          <Skeleton.Input active style={{ width: 170, height: 22 }} />
          <Skeleton.Input active size="small" style={{ width: 280, height: 16 }} />
        </div>
        <Skeleton.Button active style={{ width: 112, height: 32, borderRadius: 6 }} />
      </div>

      <div className="access-verification-skeleton__table">
        <div className="access-verification-skeleton__table-head">
          <Skeleton.Input active size="small" style={{ width: 72, height: 12 }} />
          <Skeleton.Input active size="small" style={{ width: 88, height: 12 }} />
          <Skeleton.Input active size="small" style={{ width: 64, height: 12 }} />
        </div>
        <AuthorizedUsersSkeleton rows={4} />
      </div>
    </section>
  </div>
);

/** One radio card: dot + title + description. */
function PolicyCardSkeleton({ descWidth }: { descWidth: number }) {
  return (
    <div className="access-policy-card access-verification-skeleton__policy">
      <Skeleton.Avatar active size={16} shape="circle" />
      <div className="access-verification-skeleton__policy-text">
        <Skeleton.Input active size="small" style={{ width: 128, minWidth: 128, height: 16 }} />
        <Skeleton.Input
          active
          size="small"
          style={{ width: descWidth, minWidth: descWidth, height: 14 }}
        />
      </div>
    </div>
  );
}
